import { FC, useEffect, useRef } from 'react';
import styled from 'styled-components';
import navigationItems from '~/common/constants/navigation';
import { Box } from '~/components/wrappers/Box';
import gsap from 'gsap';
import { Flex } from '~/components/wrappers/Flex';
import { SocialWrapper } from '../footer';
import { NAV_ICONS_ABBREVIATIONS_TO_NAMES } from '~/components/navigations/NavigationItems';
import { NavLink } from '@remix-run/react';
import { Routes } from '~/common/constants/routes';
import { colors } from '~/theme/colors';
import { Colors } from '~/theme';
import { StyledNavigationItem } from '../header';
import { Badge } from '~/components/badge';
import { useCartSSR } from '~/recoil/useCartSSR';
import BagIcon from '../header/BagIcon';
import { useNavigation } from '~/common/hooks/useNavigation';

const MobileMenu = ({
  isOpen,
  activeTab,
}: {
  isOpen?: boolean;
  activeTab?: string;
}) => {
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const { goToHome, goTo } = useNavigation();
  const [{ count }] = useCartSSR();

  useEffect(() => {
    const menu = mobileMenuRef.current;
    if (!menu) return;

    if (isOpen) {
      document.body.style.overflow = 'hidden';

      gsap.to(menu, {
        duration: 0,
        y: 0,
        ease: 'power2.out',
      });
    } else {
      document.body.style.overflow = 'auto';

      gsap.to(menu, {
        duration: 0,
        y: '100%',
        ease: 'power2.out',
      });
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  return (
    <MobileMenuContainer ref={mobileMenuRef}>
      <Flex width="100%" justifyContent="space-between" alignItems="center">
        <LogoWrapper onClick={goToHome}>
          <Logo
            src="/logo-pink-sun.svg"
            alt="Logo of broken planet"
            loading="lazy"
          />
        </LogoWrapper>
        <Badge count={count} backgroundColor="#F0A0BF">
          <StyledNavigationItem
            color="text"
            to={Routes.Cart}
            onClick={() => {
              goTo(Routes.Cart);
            }}
          >
            <BagIcon fill="black" />
          </StyledNavigationItem>
        </Badge>
      </Flex>
      <Flex
        width="100%"
        direction="column"
        alignItems="center"
        justifyContent="space-between"
        minHeight="22.5rem"
      >
        {navigationItems.map((item) => {
          const IconComponent = NAV_ICONS_ABBREVIATIONS_TO_NAMES[
            item.title as keyof typeof NAV_ICONS_ABBREVIATIONS_TO_NAMES
          ] as FC<{ fontColor?: string; active?: boolean }>;
          return (
            <StyledBox key={item.title}>
              <NavLink to={item.location}>
                {IconComponent ? (
                  <IconComponent
                    fontColor={colors.text as Colors}
                    active={activeTab === item.location}
                  />
                ) : null}
              </NavLink>
            </StyledBox>
          );
        })}
      </Flex>
      {/* Comment out for now - maybe will be needed later */}
      {/* <StyledWrapper>
        <Social />
      </StyledWrapper> */}
    </MobileMenuContainer>
  );
};

export const Logo = styled.img`
  width: 4.5rem;
  height: 4.5rem;
  cursor: pointer;

  @media ${({ theme }) => theme.breakpoints.md} {
    height: 2.875rem;
    width: 2.875rem;
  }
`;

const LogoWrapper = styled.button`
  background-color: transparent;
  border: none;
  box-shadow: none;
  padding: 0;
  height: 4.5rem;
  width: 4.5rem;

  @media ${({ theme }) => theme.breakpoints.md} {
    height: 2.875rem;
    width: 2.875rem;
  }
`;

const StyledBox = styled(Box)`
  :not(:last-of-type) {
    margin-bottom: 1.5rem;
  }
`;

const StyledWrapper = styled(SocialWrapper)`
  @media (max-height: 23.4375rem) {
    position: relative;
    width: 100%;
    display: flex;
    justify-content: flex-end;
  }
`;

const MobileMenuContainer = styled(Box)`
  position: fixed;
  z-index: 1;
  background-color: ${({ theme }) => theme.colors.white};
  bottom: 0;
  top: 0;
  width: 100vw;
  height: 100dvh;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
  display: flex;
  transform: translateY(100%);
  box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.5);
  overflow-y: hidden;
  padding: 1rem 1rem 4.375rem;

  @media (max-height: 23.4375rem) {
    display: block;
    overflow-y: scroll;
  }
`;

export default MobileMenu;
